import React, { useState } from "react";
import { oracleApi, setAuthToken } from "@/api/oracleClient";

export default function AuthPanel({ open, onClose, onAuthed, toast }) {
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr("");
    if (!email || !password) {
      setErr("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      const call =
        mode === "login"
          ? oracleApi.authLogin({ email, password })
          : oracleApi.authRegister({ email, password, name: name || undefined });
      const { data } = await call;
      if (data?.access_token) {
        setAuthToken(data.access_token);
        onAuthed?.(data.user);
        toast?.("success", `Signed in as ${data.user?.email} (${data.user?.role})`, "Welcome");
        onClose?.();
      } else {
        setErr("No token returned.");
      }
    } catch (e) {
      const d = e?.response?.data;
      let msg = e?.message || "Authentication failed.";
      if (typeof d?.detail === "string") msg = d.detail;
      else if (Array.isArray(d?.detail) && d.detail[0]?.msg) msg = d.detail[0].msg;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="oracle-modal-backdrop" onClick={onClose} data-testid="auth-modal">
      <div className="oracle-modal" onClick={(e) => e.stopPropagation()}>
        <div className="oracle-modal__head">
          <div className="oracle-modal__title">
            {mode === "login" ? "Sign in to ORACLE" : "Create ORACLE Account"}
          </div>
          <button className="oracle-modal__x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form className="oracle-modal__body" onSubmit={submit}>
          {mode === "register" && (
            <label className="oracle-field">
              <span>Display Name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                data-testid="auth-name"
              />
            </label>
          )}
          <label className="oracle-field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
              data-testid="auth-email"
            />
          </label>
          <label className="oracle-field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              minLength={6}
              required
              data-testid="auth-password"
            />
          </label>

          {err && <div className="oracle-error" role="alert">{err}</div>}

          <div className="oracle-modal__actions">
            <button
              type="button"
              className="oracle-btn oracle-btn--ghost"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="auth-toggle"
            >
              {mode === "login" ? "Need an account? Register" : "Have an account? Sign in"}
            </button>
            <button
              type="submit"
              className="oracle-btn oracle-btn--gold"
              disabled={loading}
              data-testid="auth-submit"
            >
              {loading ? "…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </div>
          <p className="oracle-modal__note">
            Sessions persist across refresh. First user becomes <b>admin</b>; subsequent users are{" "}
            <b>analyst</b>.
          </p>
        </form>
      </div>
    </div>
  );
}
